/**
 * @fileoverview Dammen Lobby Screen
 * @description Game lobby with mode/difficulty selection, stats, challenges
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Alert,
  Modal,
  FlatList,
  Image as RNImage,
  StyleSheet,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useRouter, useFocusEffect } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  DammenLeaderboard,
  DammenUserStats,
  DammenWeekendConfig,
  DammenGameConfig,
  DEFAULT_DAMMEN_WEEKEND_CONFIG,
  DEFAULT_DAMMEN_GAME_CONFIG,
  EMPTY_DAMMEN_STATS,
  PlayerId,
  AIDifficulty,
  initializeBoard,
} from "@/modules/dammen";

type GameMode = "free" | "weekend";
type SessionType = "solo" | "versus";

interface FamilyMember {
  id: string;
  full_name: string;
  avatar_url: string | null;
  team_id?: string;
  team_name?: string;
}

/**
 * Character SVG images
 */
const CHARACTER_IMAGES: Record<PlayerId, any> = {
  opa: require("@/assets/games/opa_endless_runner.svg"),
  oma: require("@/assets/games/omam_endless_runner.svg"),
};

/**
 * Dammen Lobby Screen
 */
export default function DammenLobbyScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeProfile, activeWeekend, userTeam } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMode, setSelectedMode] = useState<GameMode>("free");
  const [selectedSessionType, setSelectedSessionType] = useState<SessionType>("solo");
  const [selectedDifficulty, setSelectedDifficulty] = useState<AIDifficulty>("medium");
  const [myStats, setMyStats] = useState<DammenUserStats>(EMPTY_DAMMEN_STATS);
  const [gameConfig, setGameConfig] = useState<DammenGameConfig>(DEFAULT_DAMMEN_GAME_CONFIG);
  const [weekendConfig, setWeekendConfig] = useState<DammenWeekendConfig>(DEFAULT_DAMMEN_WEEKEND_CONFIG);
  const [gameEnabledForWeekend, setGameEnabledForWeekend] = useState(false);
  const [playsToday, setPlaysToday] = useState(0);
  const [leaderboardKey, setLeaderboardKey] = useState(0);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter family members by search query
  const filteredMembers = familyMembers.filter((member) =>
    member.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Determine if weekend mode is available
  const isWeekendModeAvailable = !!activeWeekend && !!userTeam && gameEnabledForWeekend;
  const weekendUnavailableReason = !activeWeekend
    ? "Geen actief weekend"
    : !userTeam
    ? "Je zit nog niet in een team"
    : !gameEnabledForWeekend
    ? "Niet geactiveerd voor dit weekend"
    : "";

  /**
   * Fetch all data
   */
  const fetchData = useCallback(async () => {
    if (!activeProfile?.id) return;

    try {
      // Fetch game config from game_definitions
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config, default_points_config")
        .eq("slug", "dammen")
        .single();

      if (gameDef?.default_config) {
        setGameConfig({
          ...DEFAULT_DAMMEN_GAME_CONFIG,
          ...gameDef.default_config,
        });
      }

      // Fetch weekend game config if active weekend exists
      if (activeWeekend) {
        const { data: weekendGame } = await supabase
          .from("weekend_game_config")
          .select("is_enabled, points_config, custom_config")
          .eq("weekend_id", activeWeekend.id)
          .eq("game_slug", "dammen")
          .single();

        setGameEnabledForWeekend(weekendGame?.is_enabled ?? false);

        if (weekendGame?.is_enabled) {
          setWeekendConfig({
            ...DEFAULT_DAMMEN_WEEKEND_CONFIG,
            ...(weekendGame.points_config as any),
            ...(weekendGame.custom_config as any),
          });

          // Fetch plays today
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const { count } = await supabase
            .from("game_sessions")
            .select("id", { count: "exact", head: true })
            .eq("created_by", activeProfile.id)
            .eq("game_slug", "dammen")
            .eq("mode", "weekend")
            .gte("created_at", today.toISOString());

          setPlaysToday(count || 0);
        }
      } else {
        setGameEnabledForWeekend(false);
      }

      // Fetch user stats
      const { data: statsData } = await supabase
        .from("user_game_stats")
        .select("games_played, games_won, games_lost, games_drawn, stats")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "dammen")
        .eq("mode", selectedMode)
        .single();

      if (statsData) {
        setMyStats({
          ...EMPTY_DAMMEN_STATS,
          ...statsData.stats,
          games_played: statsData.games_played || 0,
          games_won: statsData.games_won || 0,
          games_lost: statsData.games_lost || 0,
          games_drawn: statsData.games_drawn || 0,
          win_rate:
            statsData.games_played > 0
              ? Math.round((statsData.games_won / statsData.games_played) * 100)
              : 0,
        });
      } else {
        setMyStats(EMPTY_DAMMEN_STATS);
      }
    } catch (error) {
      console.error("Error fetching Dammen data:", error);
    } finally {
      setLoading(false);
    }
  }, [activeProfile?.id, selectedMode, activeWeekend?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Refresh data when screen gains focus
   */
  useFocusEffect(
    useCallback(() => {
      fetchData();
      setLeaderboardKey((prev) => prev + 1);
    }, [fetchData])
  );

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setLeaderboardKey((prev) => prev + 1);
    setRefreshing(false);
  }, [fetchData]);

  /**
   * Start a new solo game
   */
  const handleStartSoloGame = async () => {
    if (!activeProfile?.id) return;

    try {
      // Check if can play in weekend mode
      if (selectedMode === "weekend" && !weekendConfig.allow_unlimited_plays) {
        if (playsToday >= weekendConfig.max_plays_per_day) {
          Alert.alert(
            "Limiet bereikt",
            `Je hebt vandaag al ${playsToday} spellen gespeeld. Morgen kun je weer spelen!`
          );
          return;
        }
      }

      // Randomly assign Opa or Oma to player
      const myPiece: PlayerId = Math.random() < 0.5 ? "opa" : "oma";

      // Create game session
      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          created_by: activeProfile.id,
          game_slug: "dammen",
          mode: selectedMode,
          session_type: "solo",
          status: "active",
          started_at: new Date().toISOString(),
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
          participants: [
            {
              user_id: activeProfile.id,
              display_name: activeProfile.nickname || activeProfile.full_name,
              avatar_url: activeProfile.avatar_url,
            },
          ],
          game_state: {
            board: initializeBoard(),
            currentTurn: "opa", // Opa always starts
            playerAssignments: {
              [activeProfile.id]: myPiece,
            },
            moveHistory: [],
            capturedPieces: { opa: 0, oma: 0 },
            status: "playing",
            isAIGame: true,
            aiDifficulty: selectedDifficulty,
            timePerMove: selectedMode === "weekend" 
              ? weekendConfig.time_per_move 
              : gameConfig.time_per_move,
          },
        })
        .select("id")
        .single();

      if (error) throw error;

      // Navigate to game
      router.push({
        pathname: "/(tabs)/games/dammen/[sessionId]",
        params: {
          sessionId: session.id,
          mode: selectedMode,
        },
      });
    } catch (error) {
      console.error("Error starting Dammen game:", error);
      Alert.alert("Fout", "Kon spel niet starten. Probeer het opnieuw.");
    }
  };

  /**
   * Fetch family members for challenge
   * For weekend mode, only show members from different teams
   */
  const fetchFamilyMembers = useCallback(async () => {
    if (!activeProfile?.id) return;

    setLoadingMembers(true);
    try {
      // Get all profiles except current user
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .neq("id", activeProfile.id);

      if (selectedMode === "weekend" && activeWeekend) {
        // Get team memberships for this weekend
        const { data: teamMembers } = await supabase
          .from("team_members")
          .select("user_id, team:teams!inner(id, name)")
          .eq("teams.weekend_id", activeWeekend.id);

        // Map team info to profiles and filter out same team
        const membersWithTeams: FamilyMember[] = (profiles || []).map((p) => {
          const membership = teamMembers?.find((tm) => tm.user_id === p.id);
          const teamRaw = membership?.team;
          const team =
            teamRaw && typeof teamRaw === "object" && !Array.isArray(teamRaw)
              ? (teamRaw as { id: string; name: string })
              : null;
          return {
            id: p.id,
            full_name: p.full_name,
            avatar_url: p.avatar_url,
            team_id: team?.id,
            team_name: team?.name,
          };
        });

        // Filter to only show players from other teams
        const eligibleMembers = membersWithTeams.filter(
          (m) => m.team_id && m.team_id !== userTeam?.id
        );
        setFamilyMembers(eligibleMembers);
      } else {
        // Free play - show everyone
        setFamilyMembers(profiles || []);
      }
    } catch (err) {
      console.error("Error fetching family members:", err);
    } finally {
      setLoadingMembers(false);
    }
  }, [activeProfile?.id, selectedMode, activeWeekend, userTeam?.id]);

  /**
   * Open challenge modal
   */
  const handleChallenge = () => {
    setSearchQuery("");
    setShowChallengeModal(true);
    fetchFamilyMembers();
  };

  /**
   * Handle challenging a specific user
   */
  const handleChallengeUser = async (opponent: FamilyMember) => {
    if (!activeProfile?.id) return;

    setShowChallengeModal(false);

    try {
      // Randomly assign Opa or Oma to players
      const challengerPiece: PlayerId = Math.random() < 0.5 ? "opa" : "oma";
      const opponentPiece: PlayerId = challengerPiece === "opa" ? "oma" : "opa";

      // Create game session
      const { data: session, error } = await supabase
        .from("game_sessions")
        .insert({
          created_by: activeProfile.id,
          game_slug: "dammen",
          mode: selectedMode,
          session_type: "versus",
          status: "waiting",
          weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
          participants: [
            {
              user_id: activeProfile.id,
              display_name: activeProfile.nickname || activeProfile.full_name,
              avatar_url: activeProfile.avatar_url,
              team_id: userTeam?.id,
            },
            {
              user_id: opponent.id,
              display_name: opponent.full_name,
              avatar_url: opponent.avatar_url,
              team_id: opponent.team_id,
            },
          ],
          game_state: {
            board: initializeBoard(),
            currentTurn: "opa", // Opa always starts
            playerAssignments: {
              [activeProfile.id]: challengerPiece,
              [opponent.id]: opponentPiece,
            },
            moveHistory: [],
            capturedPieces: { opa: 0, oma: 0 },
            status: "waiting",
            isAIGame: false,
            challenged_user_id: opponent.id,
            challenged_user_name: opponent.full_name,
            challenger_id: activeProfile.id,
            challenger_name: activeProfile.nickname || activeProfile.full_name,
            challenger_ready: false,
            challenged_ready: false,
            timePerMove: selectedMode === "weekend"
              ? weekendConfig.time_per_move
              : gameConfig.time_per_move,
          },
        })
        .select("id")
        .single();

      if (error) throw error;

      // Send notification to the opponent
      await supabase.from("notifications").insert({
        user_id: opponent.id,
        type: "game_challenge",
        notification_type: "game_challenge",
        title: `🎮 ${activeProfile.nickname || activeProfile.full_name} daagt je uit!`,
        body: `${activeProfile.nickname || activeProfile.full_name} wil een potje Dammen spelen. Accepteer je de uitdaging?`,
        action_url: `/(tabs)/games/dammen/${session.id}`,
        action_data: {
          session_id: session.id,
          game_slug: "dammen",
          challenger_id: activeProfile.id,
          challenger_name: activeProfile.nickname || activeProfile.full_name,
        },
        sender_id: activeProfile.id,
        weekend_id: selectedMode === "weekend" ? activeWeekend?.id : null,
        lottie_animation: "game-controller",
        priority: "high",
      });

      // Navigate to game (will show waiting screen)
      router.push({
        pathname: "/(tabs)/games/dammen/[sessionId]",
        params: {
          sessionId: session.id,
          mode: selectedMode,
        },
      });
    } catch (error) {
      console.error("Error creating challenge:", error);
      Alert.alert("Fout", "Kon uitdaging niet versturen. Probeer het opnieuw.");
    }
  };

  /**
   * Calculate potential points for weekend mode
   */
  const getPotentialPoints = (): string => {
    const min = weekendConfig.points_participation;
    const max = weekendConfig.points_win;
    return `${min} - ${max}`;
  };

  /**
   * Can play check
   */
  const canPlay = (): boolean => {
    if (selectedMode === "free") return true;
    if (!isWeekendModeAvailable) return false;
    if (weekendConfig.allow_unlimited_plays) return true;
    return playsToday < weekendConfig.max_plays_per_day;
  };

  if (loading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["top", "left", "right"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top", "left", "right"]}
    >
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Game Description */}
        <View
          className="mx-4 mt-4 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface }}
        >
          <View className="flex-row items-center mb-2">
            <View className="flex-row -space-x-2">
              <Image
                source={CHARACTER_IMAGES.opa}
                style={{ width: 32, height: 32, borderRadius: 16 }}
                contentFit="contain"
              />
              <Image
                source={CHARACTER_IMAGES.oma}
                style={{ width: 32, height: 32, borderRadius: 16, marginLeft: -8 }}
                contentFit="contain"
              />
            </View>
            <Text
              className="ml-3 text-lg font-bold"
              style={{ color: colors.text }}
            >
              Dammen
            </Text>
          </View>
          <Text style={{ color: colors.textSecondary }}>
            Het klassieke bordspel! Sla alle stenen van je tegenstander om te winnen.
            Normale stenen bewegen diagonaal vooruit, dam-stenen (met ster) kunnen alle kanten op.
          </Text>
        </View>

        {/* Mode Selector */}
        <View className="mx-4 mt-4 flex-row gap-3">
          <TouchableOpacity
            className="flex-1 p-4 rounded-xl"
            style={{
              backgroundColor:
                selectedMode === "free" ? colors.primary : colors.surface,
              borderWidth: selectedMode === "free" ? 0 : 1,
              borderColor: colors.border,
            }}
            onPress={() => setSelectedMode("free")}
            accessibilityRole="button"
            accessibilityLabel="Vrij Spelen"
          >
            <View className="items-center">
              <Ionicons
                name="game-controller"
                size={24}
                color={selectedMode === "free" ? "#FFFFFF" : colors.primary}
              />
              <Text
                className="mt-2 font-semibold"
                style={{
                  color: selectedMode === "free" ? "#FFFFFF" : colors.text,
                }}
              >
                Vrij Spelen
              </Text>
              <Text
                className="text-xs mt-1"
                style={{
                  color:
                    selectedMode === "free"
                      ? "rgba(255,255,255,0.8)"
                      : colors.textSecondary,
                }}
              >
                Ranglijst
              </Text>
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            className="flex-1 p-4 rounded-xl"
            style={{
              backgroundColor:
                selectedMode === "weekend" ? colors.primary : colors.surface,
              borderWidth: selectedMode === "weekend" ? 0 : 1,
              borderColor: colors.border,
              opacity: isWeekendModeAvailable ? 1 : 0.5,
            }}
            onPress={() => isWeekendModeAvailable && setSelectedMode("weekend")}
            disabled={!isWeekendModeAvailable}
            accessibilityRole="button"
            accessibilityLabel="Familie Weekend"
          >
            <View className="items-center">
              <Ionicons
                name="trophy"
                size={24}
                color={selectedMode === "weekend" ? "#FFFFFF" : colors.warning}
              />
              <Text
                className="mt-2 font-semibold"
                style={{
                  color: selectedMode === "weekend" ? "#FFFFFF" : colors.text,
                }}
              >
                Familie Weekend
              </Text>
              <Text
                className="text-xs mt-1"
                style={{
                  color:
                    selectedMode === "weekend"
                      ? "rgba(255,255,255,0.8)"
                      : colors.textSecondary,
                }}
              >
                {isWeekendModeAvailable
                  ? `${getPotentialPoints()} punten`
                  : weekendUnavailableReason || "Niet actief"}
              </Text>
            </View>
          </TouchableOpacity>
        </View>

        {/* Weekend Info */}
        {selectedMode === "weekend" && isWeekendModeAvailable && (
          <View
            className="mx-4 mt-4 p-4 rounded-xl"
            style={{ backgroundColor: colors.surface }}
          >
            <View className="flex-row items-center justify-between mb-2">
              <Text style={{ color: colors.textSecondary }}>
                Vandaag gespeeld
              </Text>
              <Text className="font-semibold" style={{ color: colors.text }}>
                {playsToday} / {weekendConfig.allow_unlimited_plays ? "∞" : weekendConfig.max_plays_per_day}
              </Text>
            </View>
            <View className="flex-row items-center justify-between">
              <Text style={{ color: colors.textSecondary }}>Punten</Text>
              <Text className="font-semibold" style={{ color: colors.text }}>
                Win: {weekendConfig.points_win} • Gelijk: {weekendConfig.points_draw} • Meedoen: {weekendConfig.points_participation}
              </Text>
            </View>
          </View>
        )}

        {/* Session Type Selector (Solo vs Versus) */}
        <View className="mx-4 mt-4">
          <Text className="font-semibold mb-2" style={{ color: colors.text }}>
            Spel type
          </Text>
          <View className="flex-row gap-3">
            <TouchableOpacity
              className="flex-1 p-3 rounded-xl flex-row items-center justify-center"
              style={{
                backgroundColor:
                  selectedSessionType === "solo" ? colors.primary : colors.surface,
                borderWidth: 1,
                borderColor:
                  selectedSessionType === "solo" ? colors.primary : colors.border,
              }}
              onPress={() => setSelectedSessionType("solo")}
              accessibilityRole="button"
            >
              <Ionicons
                name="person"
                size={18}
                color={selectedSessionType === "solo" ? "#FFFFFF" : colors.text}
              />
              <Text
                className="ml-2 font-medium"
                style={{
                  color: selectedSessionType === "solo" ? "#FFFFFF" : colors.text,
                }}
              >
                Solo (vs AI)
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              className="flex-1 p-3 rounded-xl flex-row items-center justify-center"
              style={{
                backgroundColor:
                  selectedSessionType === "versus" ? colors.primary : colors.surface,
                borderWidth: 1,
                borderColor:
                  selectedSessionType === "versus" ? colors.primary : colors.border,
              }}
              onPress={() => setSelectedSessionType("versus")}
              accessibilityRole="button"
            >
              <Ionicons
                name="people"
                size={18}
                color={selectedSessionType === "versus" ? "#FFFFFF" : colors.text}
              />
              <Text
                className="ml-2 font-medium"
                style={{
                  color: selectedSessionType === "versus" ? "#FFFFFF" : colors.text,
                }}
              >
                Uitdagen
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* AI Difficulty (Solo mode only) */}
        {selectedSessionType === "solo" && (
          <View className="mx-4 mt-4">
            <Text className="font-semibold mb-2" style={{ color: colors.text }}>
              Moeilijkheid
            </Text>
            <View className="flex-row gap-2">
              {(["easy", "medium", "hard"] as AIDifficulty[]).map((diff) => (
                <TouchableOpacity
                  key={diff}
                  className="flex-1 p-3 rounded-xl items-center"
                  style={{
                    backgroundColor:
                      selectedDifficulty === diff ? colors.primary : colors.surface,
                    borderWidth: 1,
                    borderColor:
                      selectedDifficulty === diff ? colors.primary : colors.border,
                  }}
                  onPress={() => setSelectedDifficulty(diff)}
                  accessibilityRole="button"
                >
                  <Ionicons
                    name={
                      diff === "easy"
                        ? "happy"
                        : diff === "medium"
                        ? "person"
                        : "skull"
                    }
                    size={20}
                    color={selectedDifficulty === diff ? "#FFFFFF" : colors.text}
                  />
                  <Text
                    className="mt-1 text-sm font-medium"
                    style={{
                      color: selectedDifficulty === diff ? "#FFFFFF" : colors.text,
                    }}
                  >
                    {diff === "easy"
                      ? "Makkelijk"
                      : diff === "medium"
                      ? "Gemiddeld"
                      : "Moeilijk"}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        {/* My Stats */}
        <View
          className="mx-4 mt-4 p-4 rounded-xl"
          style={{ backgroundColor: colors.surface }}
        >
          <Text className="font-semibold mb-3" style={{ color: colors.text }}>
            Mijn Statistieken ({selectedMode === "weekend" ? "Weekend" : "Vrij"})
          </Text>
          <View className="flex-row flex-wrap gap-y-2">
            <View className="w-1/2">
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Gespeeld
              </Text>
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                {myStats.games_played}
              </Text>
            </View>
            <View className="w-1/2">
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Gewonnen
              </Text>
              <Text className="text-lg font-bold" style={{ color: colors.success }}>
                {myStats.games_won}
              </Text>
            </View>
            <View className="w-1/2">
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Verloren
              </Text>
              <Text className="text-lg font-bold" style={{ color: colors.error }}>
                {myStats.games_lost}
              </Text>
            </View>
            <View className="w-1/2">
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                Win Rate
              </Text>
              <Text className="text-lg font-bold" style={{ color: colors.text }}>
                {myStats.win_rate}%
              </Text>
            </View>
          </View>
        </View>

        {/* Start Button */}
        <View className="mx-4 mt-4">
          {selectedSessionType === "solo" ? (
            <TouchableOpacity
              className="p-4 rounded-xl flex-row items-center justify-center"
              style={{
                backgroundColor: canPlay() ? colors.primary : colors.border,
              }}
              onPress={handleStartSoloGame}
              disabled={!canPlay()}
              accessibilityRole="button"
              accessibilityLabel="Start spel"
            >
              <Ionicons name="play" size={24} color="#FFFFFF" />
              <Text className="ml-2 text-lg font-bold text-white">
                Start Spel
              </Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              className="p-4 rounded-xl flex-row items-center justify-center"
              style={{
                backgroundColor: canPlay() ? colors.primary : colors.border,
              }}
              onPress={handleChallenge}
              disabled={!canPlay()}
              accessibilityRole="button"
              accessibilityLabel="Daag iemand uit"
            >
              <Ionicons name="flash" size={24} color="#FFFFFF" />
              <Text className="ml-2 text-lg font-bold text-white">
                Daag Iemand Uit
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Leaderboard */}
        <View className="mt-6 mb-8">
          <DammenLeaderboard
            key={`${selectedMode}-${leaderboardKey}`}
            mode={selectedMode}
            weekendId={selectedMode === "weekend" ? activeWeekend?.id : undefined}
            maxEntries={10}
            onUserPress={(userId) => router.push(`/scores/user/${userId}`)}
          />
        </View>
      </ScrollView>

      {/* Challenge Modal */}
      <Modal
        visible={showChallengeModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowChallengeModal(false)}
      >
        <KeyboardAvoidingView
          style={styles.keyboardAvoidingView}
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={0}
        >
          <SafeAreaView style={styles.modalBackdrop} edges={["bottom"]}>
            <TouchableOpacity
              style={styles.modalBackdropTouchable}
              activeOpacity={1}
              onPress={() => setShowChallengeModal(false)}
            />
            <View style={[styles.modalContainer, { backgroundColor: colors.surface }]}>
              <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Kies een tegenstander
              </Text>
              <TouchableOpacity
                onPress={() => setShowChallengeModal(false)}
                style={styles.modalCloseButton}
              >
                <Ionicons name="close" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Search bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textSecondary} />
              <TextInput
                style={[styles.searchInput, { color: colors.text }]}
                placeholder="Zoek op naam..."
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery("")}>
                  <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
              )}
            </View>

            {/* Weekend mode info */}
            {selectedMode === "weekend" && (
              <View style={[styles.weekendInfo, { backgroundColor: colors.warning + "20" }]}>
                <Ionicons name="information-circle" size={16} color={colors.warning} />
                <Text style={[styles.weekendInfoText, { color: colors.warning }]}>
                  Je kunt alleen spelers van andere teams uitdagen
                </Text>
              </View>
            )}

            {loadingMembers ? (
              <View style={styles.modalLoading}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={[styles.modalLoadingText, { color: colors.textSecondary }]}>
                  Laden...
                </Text>
              </View>
            ) : filteredMembers.length === 0 ? (
              <View style={styles.modalEmpty}>
                <Ionicons name="people-outline" size={48} color={colors.textTertiary} />
                <Text style={[styles.modalEmptyText, { color: colors.textSecondary }]}>
                  {searchQuery
                    ? "Geen resultaten gevonden"
                    : selectedMode === "weekend"
                    ? "Geen spelers van andere teams gevonden"
                    : "Geen familieleden gevonden"}
                </Text>
              </View>
            ) : (
              <FlatList
                data={filteredMembers}
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={[styles.memberItem, { borderBottomColor: colors.border }]}
                    onPress={() => handleChallengeUser(item)}
                  >
                    {item.avatar_url ? (
                      <RNImage
                        source={{ uri: item.avatar_url }}
                        style={styles.memberAvatar}
                      />
                    ) : (
                      <View style={[styles.memberAvatarPlaceholder, { backgroundColor: colors.primary }]}>
                        <Text style={styles.memberAvatarText}>
                          {item.full_name?.charAt(0) || "?"}
                        </Text>
                      </View>
                    )}
                    <View style={styles.memberInfo}>
                      <Text style={[styles.memberName, { color: colors.text }]}>
                        {item.full_name}
                      </Text>
                      {item.team_name && (
                        <Text style={[styles.memberTeam, { color: colors.textSecondary }]}>
                          {item.team_name}
                        </Text>
                      )}
                    </View>
                    <View style={[styles.challengeButton, { backgroundColor: colors.primary }]}>
                      <Ionicons name="game-controller" size={18} color="#FFFFFF" />
                    </View>
                  </TouchableOpacity>
                )}
                  contentContainerStyle={styles.memberList}
                />
              )}
            </View>
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: {
    flex: 1,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    justifyContent: "flex-end",
  },
  modalBackdropTouchable: {
    flex: 1,
  },
  modalContainer: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: "70%",
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255, 255, 255, 0.1)",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "600",
  },
  modalCloseButton: {
    padding: 4,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: 16,
    marginTop: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: "rgba(128, 128, 128, 0.1)",
    borderRadius: 12,
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 0,
  },
  weekendInfo: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
    gap: 8,
  },
  weekendInfoText: {
    fontSize: 13,
    flex: 1,
  },
  modalLoading: {
    alignItems: "center",
    padding: 40,
  },
  modalLoadingText: {
    marginTop: 12,
    fontSize: 14,
  },
  modalEmpty: {
    alignItems: "center",
    padding: 40,
  },
  modalEmptyText: {
    marginTop: 12,
    fontSize: 14,
    textAlign: "center",
  },
  memberList: {
    paddingVertical: 8,
  },
  memberItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  memberAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  memberAvatarPlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
  },
  memberAvatarText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  memberInfo: {
    flex: 1,
    marginLeft: 12,
  },
  memberName: {
    fontSize: 16,
    fontWeight: "500",
  },
  memberTeam: {
    fontSize: 12,
    marginTop: 2,
  },
  challengeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
});

